import { useParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { useLanguage, isRTL } from "@/lib/i18n";
import { getArticle, getArticleContent } from "@/lib/blogContent";
import { ProductCTA } from "@/components/blog/ProductCTA";
import { RelatedArticles } from "@/components/blog/RelatedArticles";
import { RichArticleText } from "@/components/blog/RichArticleText";
import { Clock, ChevronRight } from "lucide-react";
import NotFound from "./NotFound";

const BASE_URL = 'https://pdrn-skin.com';

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { language, t } = useLanguage();
  const rtl = isRTL(language);

  const article = slug ? getArticle(slug) : undefined;
  const content = slug ? getArticleContent(slug, language) : undefined;

  if (!article || !content) return <NotFound />;

  const fullUrl = `${BASE_URL}/blog/${article.slug}`;

  return (
    <>
      <SEOHead
        title={`${content.title} | PDRN Skincare`}
        description={content.excerpt}
        url={fullUrl}
        image={article.image}
        type="website"
        dateModified={article.dateModified}
        speakableSelectors={['h1', 'h2', '.prose p']}
        faqItems={content.sections.slice(0, 4).map(s => ({
          question: s.heading,
          answer: s.content.slice(0, 300),
        }))}
        article={{
          headline: content.title,
          description: content.excerpt,
          datePublished: article.date,
          dateModified: article.dateModified,
          image: article.image,
          keywords: article.keywords,
          wordCount: content.sections.reduce((acc, s) => acc + s.content.split(/\s+/).length, 0),
        }}
        breadcrumbs={[
          { name: t('nav.home'), url: BASE_URL },
          { name: t('blog.title'), url: `${BASE_URL}/blog` },
          { name: content.title },
        ]}
      />
      <Header />
      <main className="min-h-screen">
        <article className="py-12 md:py-16" style={{ background: 'linear-gradient(180deg, hsl(var(--section-from)) 0%, hsl(var(--section-to)) 100%)' }}>
          <div className="container max-w-3xl">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground transition-colors">{t('nav.home')}</Link>
              <ChevronRight className={`w-3.5 h-3.5 ${rtl ? 'rotate-180' : ''}`} aria-hidden="true" />
              <Link to="/blog" className="hover:text-foreground transition-colors">{t('blog.title')}</Link>
              <ChevronRight className={`w-3.5 h-3.5 ${rtl ? 'rotate-180' : ''}`} aria-hidden="true" />
              <span className="text-foreground font-medium truncate max-w-[200px]">{content.title}</span>
            </nav>

            {/* Article Header */}
            <header className="mb-10">
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground leading-tight mb-4">
                {content.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <time dateTime={article.date}>
                  {new Date(article.date).toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })}
                </time>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                  {article.readTime} {t('blog.minRead')}
                </span>
              </div>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                {content.excerpt}
              </p>
            </header>

            {/* Hero Image */}
            {article.image && (
              <img
                src={article.image}
                alt={content.title}
                className="w-full rounded-2xl mb-10 object-cover max-h-[400px]"
                loading="eager"
              />
            )}

            {/* Article Body */}
            <div className="prose prose-lg max-w-none">
              {content.sections.map((section, idx) => (
                <section key={idx} className="mb-8">
                  <h2 className="text-xl md:text-2xl font-serif font-semibold text-foreground mb-3">
                    {section.heading}
                  </h2>
                  <p className="text-foreground/80 leading-relaxed text-base">
                    <RichArticleText text={section.content} />
                  </p>
                </section>
              ))}
            </div>

            {/* Product CTAs */}
            <ProductCTA handles={article.relatedProducts} />

            {/* Related Articles */}
            <RelatedArticles slugs={article.relatedArticles} />
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
